import React, { useEffect, useState } from 'react';
import { IconButton, Link, Panel, Stack, Text, Toggle } from '@fluentui/react';
import IvicosStrings from 'kits/language/stringKit';
import { INTEGRATION_LABEL_STYLE } from '../integrationLayout';
import { OrganizationDto } from 'services/client-api-wrapper/identity_provider/responses.dto';
import { OpenProjectDeactivationModal } from './OpenProjectDeactivationModal';
import type { IOpenProjectConfiguration } from './openproject.types';
import { OpenProjectSettingsForm, resolveOpenProjectErrorMessage } from './OpenProjectSettingsForm';

const OPENPROJECT_WEBSITE_URL = 'https://www.openproject.org';

interface IOpenProjectSettingsProps {
    isOpenProjectEnabled: boolean;
    openProjectBaseUrl: string;
    updateOpenProjectConfig: (config: IOpenProjectConfiguration) => Promise<OrganizationDto>;
    isOrgLoaded: boolean;
}

// Same flow as ZammadSettings: switching on opens a panel for the base URL, and the org is only
// configured once Save succeeds. Users then connect their own API token on the widget.
export const OpenProjectSettings: React.FC<IOpenProjectSettingsProps> = ({
    isOpenProjectEnabled,
    openProjectBaseUrl,
    updateOpenProjectConfig,
    isOrgLoaded
}) => {
    const [isToggleOn, setIsToggleOn] = useState(isOpenProjectEnabled);
    const [isSettingsPanelOpen, setIsSettingsPanelOpen] = useState(false);
    const [isDeactivationModalVisible, setIsDeactivationModalVisible] = useState(false);
    const [deactivationErrorMessage, setDeactivationErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        setIsToggleOn(isOpenProjectEnabled);
    }, [isOpenProjectEnabled]);

    const handleToggleChange = (_ev: React.MouseEvent<HTMLElement>, checked?: boolean): void => {
        setDeactivationErrorMessage(null);
        if (checked) {
            setIsToggleOn(true);
            setIsSettingsPanelOpen(true);
            return;
        }
        if (isOpenProjectEnabled) {
            setIsDeactivationModalVisible(true);
        } else {
            setIsToggleOn(false);
        }
    };

    const handleConfirmDeactivate = async (): Promise<void> => {
        setIsDeactivationModalVisible(false);
        setDeactivationErrorMessage(null);
        try {
            await updateOpenProjectConfig({});
        } catch (err) {
            console.error('Failed to disable OpenProject integration:', err);
            setDeactivationErrorMessage(resolveOpenProjectErrorMessage(err));
        }
    };

    return (
        <Stack tokens={{ childrenGap: 4 }}>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
                <Stack style={INTEGRATION_LABEL_STYLE}>
                    <Link
                        href={OPENPROJECT_WEBSITE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontWeight: 600 }}
                        data-test-id="openproject-website-link"
                    >
                        {IvicosStrings.openProjectSettingsToggleLabel}
                    </Link>
                </Stack>
                <Stack.Item grow />
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                    <Toggle
                        checked={isToggleOn}
                        onText={IvicosStrings.providerEnabledLabel}
                        offText={IvicosStrings.providerDisabledLabel}
                        disabled={!isOrgLoaded}
                        onChange={handleToggleChange}
                        styles={{ root: { minWidth: 120, marginBottom: 0 } }}
                        data-test-id="enable-openproject-integration"
                    />
                    {isToggleOn && (
                        <>
                            <IconButton
                                iconProps={{ iconName: 'Settings' }}
                                onClick={() => setIsSettingsPanelOpen(true)}
                                data-test-id="openproject-settings-icon"
                            />
                            {isOpenProjectEnabled ? (
                                <Text style={{ color: '#107C10', fontWeight: 600 }}>{IvicosStrings.openProjectSettingsConfiguredStatus}</Text>
                            ) : (
                                <Text style={{ color: '#A80000', fontWeight: 600 }}>{IvicosStrings.openProjectSettingsNotConfiguredStatus}</Text>
                            )}
                        </>
                    )}
                </Stack>
            </Stack>
            {deactivationErrorMessage && (
                <Text style={{ color: '#A80000' }} data-test-id="openproject-deactivation-error">
                    {deactivationErrorMessage}
                </Text>
            )}
            <Panel
                headerText={IvicosStrings.openProjectSettingsToggleLabel}
                isOpen={isSettingsPanelOpen}
                onDismiss={() => setIsSettingsPanelOpen(false)}
                closeButtonAriaLabel="Close"
                data-test-id="openproject-settings-panel"
            >
                <OpenProjectSettingsForm
                    openProjectBaseUrl={openProjectBaseUrl}
                    isOpen={isSettingsPanelOpen}
                    updateOpenProjectConfig={updateOpenProjectConfig}
                    onSaveSuccess={() => setIsSettingsPanelOpen(false)}
                    onCancel={() => setIsSettingsPanelOpen(false)}
                />
            </Panel>
            <OpenProjectDeactivationModal
                isOpen={isDeactivationModalVisible}
                onCancel={() => setIsDeactivationModalVisible(false)}
                onConfirm={handleConfirmDeactivate}
            />
        </Stack>
    );
};
